import { apiFetch } from "../../api/apiFetch";
import { isPrintAgentAvailable, getPrintModePreference } from "./printAgent";

export interface NetworkPrinter {
    _id: string;
    agentId: string;
    systemName: string;
    friendlyName: string;
    warehouse: string;
    hostname: string;
    status: "online" | "offline";
    mobileEnabled: boolean;
}

/** Fetch all mobile-enabled network printers */
export async function fetchNetworkPrinters(): Promise<NetworkPrinter[]> {
    try {
        const res = await apiFetch(`/printers/all?mobile=true`, {
        });
        if (!res.ok) return [];
        const data = await res.json();
        return (data || []).map((p: any) => ({
            _id: p._id,
            agentId: p.AgentId,
            systemName: p.SystemName,
            friendlyName: p.FriendlyName || p.SystemName,
            warehouse: p.Warehouse,
            hostname: p.Hostname,
            status: p.Status,
            mobileEnabled: p.MobileEnabled,
        }));
    } catch {
        return [];
    }
}

/** Submit a print job to a network printer via the backend.
 *  Pass `barcodeValue` when printing a barcode label — if the target agent
 *  has a Zebra printer it will use the fast ZPL path instead of HTML/PDF.
 *  Pass `dispose` to render the wipe DISPOSE banner on the ZPL label.
 *  `opts.labelWidthMm/labelHeightMm` carry the per-warehouse label stock
 *  size to the ZPL path (agent ≥ 1.5.0 — without them the agent assumes
 *  65×38 and shorter stock spills onto a second label); `opts.name` renders
 *  the big-name box-label layout. `opts.zpl` carries a raw ZPL document
 *  printed verbatim on a Zebra (agent ≥ 1.6.0; `html` may be "" then).
 *  A DESIGNED label (inbound pallets) must be sent as BOTH `html` and
 *  `opts.zpl` with NO barcodeValue: agents ≥ 1.6.4 (and the headless Pi
 *  agent, which has no HTML renderer) print the ZPL on a Zebra and the HTML
 *  on anything else. A barcodeValue always selects the agent's own generic
 *  65×38 layout, whatever the HTML looks like. */
export async function submitNetworkPrintJob(
    agentId: string,
    printer: string,
    html: string,
    copies: number = 1,
    barcodeValue: string | null = null,
    dispose: boolean = false,
    opts: { name?: string; footer?: string; labelWidthMm?: number; labelHeightMm?: number; zpl?: string } = {}
): Promise<{ success: boolean; jobId?: string; error?: string }> {
    try {
        const res = await apiFetch(`/printers/print`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                agentId, printer, html: html || null, copies, barcodeValue, dispose,
                ...(opts.name ? { name: opts.name } : {}),
                ...(opts.footer ? { footer: opts.footer } : {}),
                ...(opts.labelWidthMm && opts.labelWidthMm > 0 ? { labelWidth: opts.labelWidthMm } : {}),
                ...(opts.labelHeightMm && opts.labelHeightMm > 0 ? { labelHeight: opts.labelHeightMm } : {}),
                ...(opts.zpl ? { zpl: opts.zpl } : {}),
            }),
        });
        const data = await res.json();
        if (res.ok && data.jobId) {
            return { success: true, jobId: data.jobId };
        }
        return { success: false, error: data.error || "Failed to queue print job" };
    } catch {
        return { success: false, error: "Could not reach server" };
    }
}

/** Check print job status */
export async function checkPrintJobStatus(
    jobId: string
): Promise<{ status: string; error?: string }> {
    try {
        const res = await apiFetch(`/printers/jobs/${jobId}/status`, {
        });
        if (!res.ok) return { status: "unknown" };
        return await res.json();
    } catch {
        return { status: "unknown" };
    }
}

/** Get/set the saved network printer selection from localStorage */
export function getSavedNetworkPrinter(): { agentId: string; printerName: string; friendlyName: string; warehouse: string } | null {
    try {
        const stored = localStorage.getItem("network-printer-selection");
        return stored ? JSON.parse(stored) : null;
    } catch { return null; }
}

export function saveNetworkPrinter(selection: { agentId: string; printerName: string; friendlyName: string; warehouse: string }) {
    localStorage.setItem("network-printer-selection", JSON.stringify(selection));
}

export function clearNetworkPrinter() {
    localStorage.removeItem("network-printer-selection");
}

/** Resolve which print route this device should use. The user's explicit
 *  choice from the printer-select modal wins whenever its route is usable —
 *  in particular, "Local" holds even while a previously-saved network
 *  printer is still online (before this, detection always preferred the
 *  network printer and devices silently switched back to it). Falls back
 *  to network-then-local when no preference is stored (or the preferred
 *  route is down), and null when nothing is reachable. */
export async function detectPrintMode(): Promise<"local" | "network" | null> {
    const pref = getPrintModePreference();
    if (pref === "local" && await isPrintAgentAvailable()) return "local";
    if (getSavedNetworkPrinter() && await isSavedNetworkPrinterOnline()) return "network";
    if (await isPrintAgentAvailable()) return "local";
    return null;
}

/** Check if the saved network printer is currently online */
export async function isSavedNetworkPrinterOnline(): Promise<boolean> {
    const saved = getSavedNetworkPrinter();
    if (!saved) return false;
    try {
        const printers = await fetchNetworkPrinters();
        return printers.some(
            p => p.agentId === saved.agentId && p.systemName === saved.printerName && p.status === "online"
        );
    } catch {
        return false;
    }
}
